import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { confirmDelete, handleDeleteError } from '../utils/deleteHelper';

const EMPTY = {
  owner_name: '', owner_cnic: '', owner_father_husband_name: '', owner_mobile: '',
  vehicle_chassis_number: '', hpa: '', engine_number: '', address: '',
  employee_id: '', vehicle_number: '', vehicle_model: '', vehicle_purchase_date: '',
  showroom_id: '', unit_id: '', company_id: ''
};

// CNIC format: 00000-0000000-0
const formatCNIC = (val) => {
  const d = val.replace(/\D/g, '').slice(0, 13);
  if (d.length <= 5) return d;
  if (d.length <= 12) return `${d.slice(0,5)}-${d.slice(5)}`;
  return `${d.slice(0,5)}-${d.slice(5,12)}-${d.slice(12)}`;
};

export default function EmployeeVehicles() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canWrite = ['admin', 'manager', 'boss_secretary'].includes(user?.role);
  const printRef = useRef(null);

  const [records, setRecords]       = useState([]);
  const [employees, setEmployees]   = useState([]);
  const [showrooms, setShowrooms]   = useState([]);
  const [units, setUnits]           = useState([]);
  const [companies, setCompanies]   = useState([]);
  const [form, setForm]             = useState(EMPTY);
  const [editing, setEditing]       = useState(null);
  const [viewRecord, setViewRecord] = useState(null);
  const [showForm, setShowForm]     = useState(false);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);

  // Search / filter
  const [search, setSearch]             = useState('');
  const [filterUnit, setFilterUnit]     = useState('');
  const [filterShowroom, setFilterShowroom] = useState('');

  // ── Load all reference data ───────────────────────────────────────────────
  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const r = await api.get('/api/employee-vehicles'); setRecords(r.data); }
    catch { setRecords([]); }
    try { const r = await api.get('/api/employees/active'); setEmployees(r.data); }
    catch { setEmployees([]); }
    try { const r = await api.get('/api/showrooms/active'); setShowrooms(r.data); }
    catch { setShowrooms([]); }
    try { const r = await api.get('/api/units'); setUnits(r.data); }
    catch { setUnits([]); }
    if (isAdmin) {
      try { const r = await api.get('/api/companies'); setCompanies(r.data); }
      catch { setCompanies([]); }
    }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user?.role]);

  // ── Filter / search ───────────────────────────────────────────────────────
  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || [r.owner_name, r.vehicle_number, r.owner_cnic,
      r.vehicle_model, r.engine_number, r.employee_name]
      .some(f => (f || '').toLowerCase().includes(q));
    const matchUnit     = !filterUnit     || String(r.unit_id) === filterUnit;
    const matchShowroom = !filterShowroom || String(r.showroom_id) === filterShowroom;
    return matchSearch && matchUnit && matchShowroom;
  });

  // ── Employee auto-fill ────────────────────────────────────────────────────
  const handleEmployeeSelect = (empId) => {
    const emp = employees.find(e => String(e.id) === String(empId));
    if (!emp) { setForm(f => ({ ...f, employee_id: empId })); return; }
    // Auto-fill department → unit
    const matchedUnit = units.find(u =>
      u.name?.toLowerCase() === (emp.unit || '').toLowerCase()
    );
    setForm(f => ({
      ...f,
      employee_id: empId,
      unit_id: matchedUnit ? String(matchedUnit.id) : f.unit_id,
      // auto-fill owner from employee if empty
      owner_name: f.owner_name || emp.name || '',
    }));
  };

  // ── Form open/close ───────────────────────────────────────────────────────
  const openAdd = () => { setForm(EMPTY); setEditing(null); setShowForm(true); };
  const openEdit = (r) => {
    setForm({
      owner_name: r.owner_name || '', owner_cnic: r.owner_cnic || '',
      owner_father_husband_name: r.owner_father_husband_name || '',
      owner_mobile: r.owner_mobile || '',
      vehicle_chassis_number: r.vehicle_chassis_number || '',
      hpa: r.hpa || '', engine_number: r.engine_number || '',
      address: r.address || '',
      employee_id: r.employee_id ? String(r.employee_id) : '',
      vehicle_number: r.vehicle_number || '', vehicle_model: r.vehicle_model || '',
      vehicle_purchase_date: r.vehicle_purchase_date
        ? r.vehicle_purchase_date.split('T')[0] : '',
      showroom_id: r.showroom_id ? String(r.showroom_id) : '',
      unit_id: r.unit_id ? String(r.unit_id) : '',
      company_id: r.company_id ? String(r.company_id) : ''
    });
    setEditing(r.id); setShowForm(true);
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) await api.put(`/api/employee-vehicles/${editing}`, payload);
      else         await api.post('/api/employee-vehicles', payload);
      setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id, label) => {
    if (!confirmDelete('employee vehicle record', label)) return;
    try { await api.delete(`/api/employee-vehicles/${id}`); load(); }
    catch (err) { handleDeleteError(err); }
  };

  // ── Print record ──────────────────────────────────────────────────────────
  const handlePrint = (r) => {
    const content = `
      <html><head><title>Employee Vehicle Record</title>
      <style>
        body{font-family:Arial,sans-serif;font-size:12px;margin:20px}
        h2{border-bottom:2px solid #333;padding-bottom:6px}
        table{width:100%;border-collapse:collapse;margin-top:10px}
        td{padding:5px 8px;border:1px solid #ddd;vertical-align:top}
        td:first-child{font-weight:bold;background:#f5f5f5;width:35%}
        .header{display:flex;justify-content:space-between;align-items:center}
      </style></head><body>
      <div class="header">
        <h2>Employee Vehicle Record</h2>
        <span>${new Date().toLocaleDateString('en-PK')}</span>
      </div>
      <table>
        <tr><td>Company</td><td>${r.company_name || '—'}</td></tr>
        <tr><td>Unit</td><td>${r.unit_name || '—'}</td></tr>
        <tr><td>Employee Name</td><td>${r.employee_name || '—'}</td></tr>
        <tr><td>Department</td><td>${r.department || '—'}</td></tr>
        <tr><td>Employee Code</td><td>${r.employee_code || '—'}</td></tr>
        <tr><td colspan="2" style="background:#e8f0fe;font-weight:bold">Owner Details</td></tr>
        <tr><td>Owner Name</td><td>${r.owner_name || '—'}</td></tr>
        <tr><td>Owner CNIC</td><td>${r.owner_cnic || '—'}</td></tr>
        <tr><td>Father / Husband Name</td><td>${r.owner_father_husband_name || '—'}</td></tr>
        <tr><td>Mobile</td><td>${r.owner_mobile || '—'}</td></tr>
        <tr><td>Address</td><td>${r.address || '—'}</td></tr>
        <tr><td colspan="2" style="background:#e8f0fe;font-weight:bold">Vehicle Details</td></tr>
        <tr><td>Vehicle Number</td><td>${r.vehicle_number || '—'}</td></tr>
        <tr><td>Vehicle Model</td><td>${r.vehicle_model || '—'}</td></tr>
        <tr><td>Purchase Date</td><td>${r.vehicle_purchase_date ? new Date(r.vehicle_purchase_date).toLocaleDateString('en-PK') : '—'}</td></tr>
        <tr><td>Chassis Number</td><td>${r.vehicle_chassis_number || '—'}</td></tr>
        <tr><td>Engine Number</td><td>${r.engine_number || '—'}</td></tr>
        <tr><td>HPA</td><td>${r.hpa || '—'}</td></tr>
        <tr><td>Showroom</td><td>${r.showroom_name || '—'} ${r.showroom_city ? '— ' + r.showroom_city : ''}</td></tr>
        <tr><td>Registered On</td><td>${new Date(r.created_at).toLocaleDateString('en-PK')}</td></tr>
      </table>
      </body></html>
    `;
    const win = window.open('', '_blank');
    win.document.write(content);
    win.document.close();
    win.print();
  };

  // ── Export Excel (SheetJS CDN) ────────────────────────────────────────────
  const handleExportExcel = () => {
    if (!window.XLSX) { alert('SheetJS not loaded — check CDN'); return; }
    const rows = filtered.map(r => ({
      'Company':             r.company_name || '',
      'Unit':                r.unit_name || '',
      'Employee Name':       r.employee_name || '',
      'Department':          r.department || '',
      'Employee Code':       r.employee_code || '',
      'Owner Name':          r.owner_name || '',
      'Owner CNIC':          r.owner_cnic || '',
      'Father/Husband Name': r.owner_father_husband_name || '',
      'Mobile':              r.owner_mobile || '',
      'Vehicle Number':      r.vehicle_number || '',
      'Vehicle Model':       r.vehicle_model || '',
      'Purchase Date':       r.vehicle_purchase_date ? r.vehicle_purchase_date.split('T')[0] : '',
      'Chassis Number':      r.vehicle_chassis_number || '',
      'Engine Number':       r.engine_number || '',
      'HPA':                 r.hpa || '',
      'Showroom':            r.showroom_name || '',
      'Showroom City':       r.showroom_city || '',
      'Address':             r.address || '',
      'Registered On':       r.created_at ? r.created_at.split('T')[0] : '',
    }));
    const ws = window.XLSX.utils.json_to_sheet(rows);
    const wb = window.XLSX.utils.book_new();
    window.XLSX.utils.book_append_sheet(wb, ws, 'Employee Vehicles');
    window.XLSX.writeFile(wb, `employee_vehicles_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ── Export CSV ────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const cols = {
      company_name:'Company', unit_name:'Unit', employee_name:'Employee',
      department:'Department', employee_code:'Emp Code', owner_name:'Owner',
      owner_cnic:'CNIC', vehicle_number:'Vehicle No', vehicle_model:'Model',
      vehicle_chassis_number:'Chassis No', engine_number:'Engine No',
      hpa:'HPA', showroom_name:'Showroom', owner_mobile:'Mobile',
    };
    const header = Object.values(cols).join(',') + '\n';
    const body = filtered.map(r =>
      Object.keys(cols).map(k => `"${r[k] || ''}"`).join(',')
    ).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `employee_vehicles_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Employee Vehicles</h1>
          <p className="text-gray-500 text-sm">{records.length} records · {filtered.length} shown</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={handleExportExcel}
            className="border border-green-600 text-green-700 hover:bg-green-50 px-3 py-2 rounded-lg text-sm font-medium">
            📊 Excel
          </button>
          <button onClick={handleExportCSV}
            className="border border-gray-300 text-gray-600 hover:bg-gray-50 px-3 py-2 rounded-lg text-sm font-medium">
            📄 CSV
          </button>
          {canWrite && (
            <button onClick={openAdd}
              className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
              ➕ Add Record
            </button>
          )}
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search owner, vehicle no, CNIC, model, employee..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <select value={filterUnit} onChange={e => setFilterUnit(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
            <option value="">All Units</option>
            {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <select value={filterShowroom} onChange={e => setFilterShowroom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
            <option value="">All Showrooms</option>
            {showrooms.map(s => <option key={s.id} value={s.id}>{s.showroom_name}</option>)}
          </select>
        </div>
        {(search || filterUnit || filterShowroom) && (
          <button onClick={() => { setSearch(''); setFilterUnit(''); setFilterShowroom(''); }}
            className="text-xs text-gray-500 hover:text-red-500 px-2 py-2">✕ Clear</button>
        )}
      </div>

      {/* ── Form Modal ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[93vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-gray-800">
                {editing ? 'Edit Employee Vehicle' : 'Add Employee Vehicle'}
              </h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }}
                className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">

              {/* ─ Employee Section ─ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 pb-1 border-b">
                  Employee Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                    <select value={form.employee_id} onChange={e => handleEmployeeSelect(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">— Select Employee —</option>
                      {employees.map(e => (
                        <option key={e.id} value={e.id}>
                          {e.name} — {e.department || 'No Dept'} {e.employee_code ? `(${e.employee_code})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                    <select value={form.unit_id} onChange={e => setForm({ ...form, unit_id: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">— Select Unit —</option>
                      {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                  {isAdmin && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                      <select value={form.company_id} onChange={e => setForm({ ...form, company_id: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">— Select Company —</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  )}
                  {!isAdmin && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                      <input type="text" disabled value={user?.company_name || 'Your Company'}
                        className="w-full border border-gray-100 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500" />
                    </div>
                  )}
                </div>
              </div>

              {/* ─ Owner Section ─ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 pb-1 border-b">
                  Owner Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name</label>
                    <input type="text" value={form.owner_name}
                      onChange={e => setForm({ ...form, owner_name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CNIC</label>
                    <input type="text" value={form.owner_cnic} placeholder="00000-0000000-0"
                      onChange={e => setForm({ ...form, owner_cnic: formatCNIC(e.target.value) })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Father / Husband Name</label>
                    <input type="text" value={form.owner_father_husband_name}
                      onChange={e => setForm({ ...form, owner_father_husband_name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                    <input type="text" value={form.owner_mobile}
                      onChange={e => setForm({ ...form, owner_mobile: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <textarea rows={2} value={form.address}
                      onChange={e => setForm({ ...form, address: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              </div>

              {/* ─ Vehicle Section ─ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 pb-1 border-b">
                  Vehicle Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Number</label>
                    <input type="text" value={form.vehicle_number}
                      onChange={e => setForm({ ...form, vehicle_number: e.target.value.toUpperCase() })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Model</label>
                    <input type="text" value={form.vehicle_model}
                      onChange={e => setForm({ ...form, vehicle_model: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
                    <input type="date" value={form.vehicle_purchase_date}
                      onChange={e => setForm({ ...form, vehicle_purchase_date: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Showroom</label>
                    <select value={form.showroom_id} onChange={e => setForm({ ...form, showroom_id: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">— Select Showroom —</option>
                      {showrooms.map(s => (
                        <option key={s.id} value={s.id}>{s.showroom_name}{s.city ? ` — ${s.city}` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Chassis Number</label>
                    <input type="text" value={form.vehicle_chassis_number}
                      onChange={e => setForm({ ...form, vehicle_chassis_number: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Engine Number</label>
                    <input type="text" value={form.engine_number}
                      onChange={e => setForm({ ...form, engine_number: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">HPA (Hire Purchase Agreement)</label>
                    <input type="text" value={form.hpa}
                      onChange={e => setForm({ ...form, hpa: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2 border-t">
                <button type="submit" disabled={saving}
                  className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-60">
                  {saving ? 'Saving...' : editing ? 'Update Record' : 'Add Record'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {viewRecord && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b flex justify-between items-center">
              <h2 className="font-bold text-gray-800 text-lg">Vehicle Record Details</h2>
              <div className="flex gap-2">
                <button onClick={() => handlePrint(viewRecord)}
                  className="text-sm text-blue-600 hover:text-blue-800 border border-blue-200 px-3 py-1 rounded-lg">
                  🖨️ Print
                </button>
                <button onClick={() => setViewRecord(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {[
                ['Company', viewRecord.company_name], ['Unit', viewRecord.unit_name],
                ['Employee', `${viewRecord.employee_name || '—'} ${viewRecord.employee_code ? `(${viewRecord.employee_code})` : ''}`],
                ['Department', viewRecord.department],
              ].map(([l,v]) => v ? (
                <div key={l} className="flex gap-2 text-sm"><span className="w-40 text-gray-500 flex-shrink-0">{l}</span><span className="font-medium text-gray-800">{v}</span></div>
              ) : null)}
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Owner Details</p>
                {[
                  ['Owner Name', viewRecord.owner_name], ['CNIC', viewRecord.owner_cnic],
                  ['Father/Husband', viewRecord.owner_father_husband_name],
                  ['Mobile', viewRecord.owner_mobile], ['Address', viewRecord.address],
                ].map(([l,v]) => v ? (
                  <div key={l} className="flex gap-2 text-sm mb-1.5"><span className="w-40 text-gray-500 flex-shrink-0">{l}</span><span className="text-gray-800">{v}</span></div>
                ) : null)}
              </div>
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Vehicle Details</p>
                {[
                  ['Vehicle Number', viewRecord.vehicle_number], ['Model', viewRecord.vehicle_model],
                  ['Purchase Date', viewRecord.vehicle_purchase_date ? new Date(viewRecord.vehicle_purchase_date).toLocaleDateString('en-PK') : null],
                  ['Chassis No.', viewRecord.vehicle_chassis_number], ['Engine No.', viewRecord.engine_number],
                  ['HPA', viewRecord.hpa],
                  ['Showroom', viewRecord.showroom_name ? `${viewRecord.showroom_name}${viewRecord.showroom_city ? ' — ' + viewRecord.showroom_city : ''}` : null],
                ].map(([l,v]) => v ? (
                  <div key={l} className="flex gap-2 text-sm mb-1.5"><span className="w-40 text-gray-500 flex-shrink-0">{l}</span><span className="font-mono text-gray-800">{v}</span></div>
                ) : null)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-4xl animate-pulse mb-2">🚗</div>
            <p>Loading records...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-5xl mb-3">🚗</div>
            <p>No employee vehicle records found</p>
            {canWrite && <button onClick={openAdd} className="mt-3 text-blue-600 text-sm hover:underline">+ Add first record</button>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Owner', 'CNIC', 'Vehicle No.', 'Model', 'Employee', 'Unit',
                    isAdmin ? 'Company' : '', 'Showroom', 'Purchase Date', 'Actions']
                    .filter(Boolean).map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">{r.owner_name || '—'}</td>
                    <td className="px-4 py-3 font-mono text-gray-600 text-xs">{r.owner_cnic || '—'}</td>
                    <td className="px-4 py-3 font-mono font-semibold text-blue-700">{r.vehicle_number || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.vehicle_model || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      <div>{r.employee_name || '—'}</div>
                      {r.department && <div className="text-gray-400">{r.department}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{r.unit_name || '—'}</td>
                    {isAdmin && <td className="px-4 py-3 text-gray-600 text-xs">{r.company_name || '—'}</td>}
                    <td className="px-4 py-3 text-gray-600 text-xs">{r.showroom_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {r.vehicle_purchase_date ? new Date(r.vehicle_purchase_date).toLocaleDateString('en-PK') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => setViewRecord(r)}
                          className="text-indigo-600 hover:text-indigo-800 text-xs font-medium">👁️ View</button>
                        {canWrite && (
                          <button onClick={() => openEdit(r)}
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium">✏️ Edit</button>
                        )}
                        <button onClick={() => handlePrint(r)}
                          className="text-gray-500 hover:text-gray-700 text-xs font-medium">🖨️</button>
                        {isAdmin && (
                          <button onClick={() => handleDelete(r.id, r.owner_name || r.vehicle_number)}
                            className="text-red-500 hover:text-red-700 text-xs font-medium">🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
